import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetNurseDashboard } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Microscope, Activity, MapPin, ArrowRight, BookOpen, Calendar, MessageSquare, GraduationCap, Plus } from "lucide-react";
import { Link } from "wouter";
import { useState, useEffect } from "react";

function AppraisalCountdown() {
  const stored = localStorage.getItem("rheumhub_doctor_appraisal_date");
  if (!stored) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Next Appraisal</CardTitle>
          <Calendar className="h-4 w-4 text-slate-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-slate-400">—</div>
          <p className="text-xs text-slate-500 mt-1">
            <Link href="/doctor/settings" className="text-indigo-600 hover:underline">Set your appraisal date</Link>
          </p>
        </CardContent>
      </Card>
    );
  }
  const target = new Date(stored);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const colour = diffDays < 30 ? "text-red-600" : diffDays < 90 ? "text-amber-600" : "text-indigo-600";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Next Appraisal</CardTitle>
        <Calendar className="h-4 w-4 text-slate-500" />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${colour}`}>
          {diffDays > 0 ? `${diffDays}d` : "Due"}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {target.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </p>
      </CardContent>
    </Card>
  );
}

function CpdCard() {
  const [summary, setSummary] = useState<{ total: number; year: number } | null>(null);

  useEffect(() => {
    fetch("/api/doctor/cpd/summary")
      .then((r) => r.json())
      .then((d) => setSummary(d))
      .catch(() => {});
  }, []);

  const credits = summary?.total ?? 0;
  const GMC_ANNUAL_TARGET = 50;
  const pct = Math.min((credits / GMC_ANNUAL_TARGET) * 100, 100);
  const colour = credits >= GMC_ANNUAL_TARGET ? "text-emerald-600" : credits >= 30 ? "text-indigo-600" : "text-amber-600";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">CPD Credits (this year)</CardTitle>
        <GraduationCap className="h-4 w-4 text-slate-500" />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${colour}`}>{credits.toFixed(1)}</div>
        <p className="text-xs text-slate-500 mt-1">of {GMC_ANNUAL_TARGET} hrs GMC target</p>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${credits >= GMC_ANNUAL_TARGET ? "bg-emerald-500" : "bg-indigo-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <Link href="/doctor/cpd">
          <button className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
            <Plus className="h-3 w-3" /> Log CPD activity
          </button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function DoctorDashboard() {
  const { data: dashboard, isLoading, error } = useGetNurseDashboard();

  if (isLoading) return <DoctorLayout title="Dashboard"><LoadingScreen /></DoctorLayout>;
  if (error || !dashboard) return <DoctorLayout title="Dashboard"><div>Error loading dashboard</div></DoctorLayout>;

  const quickCards = [
    {
      href: "/doctor/diagnostic-criteria",
      icon: Microscope,
      title: "Diagnostic Criteria",
      desc: "ACR/EULAR classification criteria for RA, SLE, axSpA, and more",
      colour: "text-indigo-600",
      bg: "bg-indigo-50",
    },
    {
      href: "/doctor/disease-activity",
      icon: Activity,
      title: "Disease Activity Scores",
      desc: "DAS28, CDAI, SDAI, SLEDAI-2K, BASDAI, BVAS calculators",
      colour: "text-violet-600",
      bg: "bg-violet-50",
    },
    {
      href: "/doctor/prescribing",
      icon: BookOpen,
      title: "Prescribing Reference",
      desc: "NICE TA criteria, biologic screening, biosimilar guidance",
      colour: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      href: "/doctor/gp-referral",
      icon: MapPin,
      title: "GP Referral Hub",
      desc: "Referral criteria, red flags, pre-referral investigations",
      colour: "text-teal-600",
      bg: "bg-teal-50",
    },
    {
      href: "/doctor/chat",
      icon: MessageSquare,
      title: "AI Clinical Assistant",
      desc: "Consultant-level queries grounded in GMC, NICE, BSR, EULAR",
      colour: "text-emerald-600",
      bg: "bg-emerald-50",
    },
  ];

  return (
    <DoctorLayout title="Dashboard">
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        <AppraisalCountdown />
        <CpdCard />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Guidelines in Reference</CardTitle>
            <BookOpen className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalGuidelines}</div>
            <p className="text-xs text-slate-500">Active NICE/BSR/EULAR/ACR guidelines</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {quickCards.map((card) => (
          <Link key={card.href} href={card.href}>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group h-full">
              <div className={`${card.bg} ${card.colour} h-10 w-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                <card.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{card.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Guidelines</CardTitle>
          <CardDescription>Latest updates from NICE, BSR, EULAR, and ACR</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {dashboard.recentGuidelines.map((g) => (
            <div key={g.id} className="flex items-center justify-between group">
              <div className="space-y-1">
                <p className="text-sm font-medium leading-none">{g.title}</p>
                <p className="text-sm text-slate-500">{g.organisation} · {g.category}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          ))}
        </CardContent>
      </Card>
    </DoctorLayout>
  );
}
